/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import type { ConsumerLookupResponse } from '@/routes/action.consumer-lookup';
import { Link } from '@/components/link';
import { CONFIDENTIALITY_NOTICE_HREF, TERMS_OF_USE_HREF } from '@/lib/main-site-legal-hrefs';
import { cn } from '@/lib/utils';
import AuthStepShell from '../auth-step-shell';
import { useLoginFlowVariant } from '../variant';

const loginLegalLinkClassName = 'font-medium text-black underline underline-offset-2';

interface EmailStepProps {
    initialEmail?: string;
    onLookupResult: (result: ConsumerLookupResponse) => void;
    /** Orchestrator-level message, e.g. no account exists for this email. */
    externalError?: string;
}

export default function EmailStep({ initialEmail, onLookupResult, externalError }: EmailStepProps): ReactElement {
    const { t } = useTranslation('login');
    const fetcher = useFetcher<ConsumerLookupResponse>();
    const isPage = useLoginFlowVariant() === 'page';
    const [termsAccepted, setTermsAccepted] = useState(false);

    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        if (!fetcher.data.error) {
            onLookupResult(fetcher.data);
        }
    }, [fetcher.data, fetcher.state, onLookupResult]);

    const heading = t('sheet.signIn', { defaultValue: 'Sign In' });
    const error = fetcher.data?.error?.message ?? externalError;

    return (
        <AuthStepShell heading={heading} step={1}>
            <fetcher.Form method="post" action="/action/consumer-lookup">
                <div className={cn(isPage ? 'mt-6 mb-8' : 'mt-4 mb-6')}>
                    <Input
                        id="sheet-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        placeholder={t('emailPlaceholder', { defaultValue: 'Enter your email' })}
                        defaultValue={initialEmail}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? 'sheet-email-error' : undefined}
                        className={cn(isPage && 'h-12 text-base sm:h-14')}
                    />
                    {error ? (
                        <p id="sheet-email-error" className="mt-2 text-sm text-status-critical" role="alert">
                            {error}
                        </p>
                    ) : null}
                </div>

                <div className={cn(isPage ? 'mb-8' : 'mb-6')}>
                    <div className="flex items-start gap-3">
                        <Checkbox
                            id="sheet-legal-terms"
                            checked={termsAccepted}
                            onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                            className="mt-0.5 shrink-0"
                            aria-describedby="sheet-legal-terms-label"
                        />
                        <p id="sheet-legal-terms-label" className="text-sm leading-snug text-black">
                            <label htmlFor="sheet-legal-terms" className="cursor-pointer font-normal">
                                {t('sheet.legalAgreementPrefix', {
                                    defaultValue: 'I have read and agree to the',
                                })}{' '}
                            </label>
                            <Link
                                to={CONFIDENTIALITY_NOTICE_HREF}
                                className={loginLegalLinkClassName}
                                onClick={(event) => event.stopPropagation()}>
                                {t('sheet.confidentialityNotice', { defaultValue: 'Confidentiality Notice' })}
                            </Link>{' '}
                            {t('sheet.legalAgreementMiddle', { defaultValue: 'and the' })}{' '}
                            <Link
                                to={TERMS_OF_USE_HREF}
                                className={loginLegalLinkClassName}
                                onClick={(event) => event.stopPropagation()}>
                                {t('sheet.websiteTermsOfUse', { defaultValue: 'Website Terms of Use' })}
                            </Link>
                            .
                        </p>
                    </div>
                </div>

                <FormSubmitButton
                    defaultText={t('sheet.continue', { defaultValue: 'Continue' })}
                    submittingText={t('sheet.continuing', { defaultValue: 'Checking...' })}
                    fetcher={fetcher}
                    disabled={!termsAccepted}
                    className={cn('h-12 w-full text-base', isPage && 'sm:h-14')}
                />
            </fetcher.Form>
        </AuthStepShell>
    );
}
